/**
 * Adapted from PlayCanvas Engine's framework/gsplat/gsplat-processor.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import type {
  GSplatResourceLease,
  IGSplatHandleBinding,
} from '@/plugins/gsplat/GSplatHandleBinding'
import type {
  GSplatGraphicsAdapter,
  GSplatProcessingPassLeaf,
  GSplatProcessingPipelineDescriptor,
  GSplatProcessingPipelineLeaf,
  GSplatProcessingTargetLeaf,
  GSplatShaderLanguage,
  GSplatTextureLeaf,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatFormat, GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import type { Disposable } from '@/shared/types/core'

const PROCESSOR_LABEL = 'GSplatProcessor'

export interface GSplatProcessorBinding {
  readonly resource?: GSplatResourceLease
  readonly handleBinding?: IGSplatHandleBinding
  readonly streams?: readonly string[]
}

export interface GSplatProcessorOptions {
  readonly processGLSL?: string
  readonly processWGSL?: string
  readonly label?: string
}

export interface GSplatProcessingSlotRequest {
  readonly label: string
  readonly run: () => void | Promise<void>
}

/** GSplatSystem 在 RenderPipeline Prepare 暴露的唯一 processing slot。 */
export interface GSplatProcessingSlot {
  enqueue(request: GSplatProcessingSlotRequest): Promise<void>
}

export type GSplatProcessorInvalidation = (
  binding: GSplatProcessorBinding,
  streams: readonly string[]
) => void

export class GSplatProcessorError extends Error {
  readonly code:
    | 'GSPLAT_PROCESSOR_INVALID_BINDING'
    | 'GSPLAT_PROCESSOR_INVALID_STREAM'
    | 'GSPLAT_PROCESSOR_READ_WRITE_OVERLAP'
    | 'GSPLAT_PROCESSOR_LIMIT_EXCEEDED'
    | 'GSPLAT_PROCESSOR_SHADER_INVALID'
    | 'GSPLAT_PROCESSOR_STALE'
    | 'GSPLAT_PROCESSOR_DISPOSED'

  constructor(code: GSplatProcessorError['code'], message: string) {
    super(message)
    this.name = 'GSplatProcessorError'
    this.code = code
  }
}

interface ResolvedBinding {
  readonly input: GSplatProcessorBinding
  readonly resource: GSplatResourceLease
  readonly identity: object
  readonly generation: number
  readonly handleGeneration: number | null
}

interface ResolvedTexture {
  readonly name: string
  readonly descriptor: GSplatStreamDescriptor
  readonly texture: GSplatTextureLeaf
}

interface ProcessSnapshot {
  readonly processorGeneration: number
  readonly adapterGeneration: number
  readonly sourceGeneration: number
  readonly destinationGeneration: number
  readonly sourceHandleGeneration: number | null
  readonly destinationHandleGeneration: number | null
  readonly sourceFormatHash: number
  readonly destinationFormatHash: number
  readonly textureBindings: readonly Readonly<{ name: string; texture: GSplatTextureLeaf }>[]
  readonly parameters: readonly (readonly [string, unknown])[]
  readonly blendState: unknown
}

/**
 * System-owned Unified stream processor。
 *
 * 高层只持有 Resource/Handle lease；MRT、shader/quad 与 pass 均由窄 adapter 私有叶子实现。
 */
export class GSplatProcessor implements Disposable {
  blendState: unknown = null

  readonly pipelineKey: string
  readonly inputStreams: readonly GSplatStreamDescriptor[]
  readonly outputStreams: readonly GSplatStreamDescriptor[]

  private readonly adapter: GSplatGraphicsAdapter
  private readonly slot: GSplatProcessingSlot
  private readonly invalidate?: GSplatProcessorInvalidation
  private readonly label: string
  private readonly source: ResolvedBinding
  private readonly destination: ResolvedBinding
  private readonly sourceFormat: GSplatFormat
  private readonly destinationFormat: GSplatFormat
  private readonly adapterGeneration: number
  private readonly sourceFormatHash: number
  private readonly destinationFormatHash: number
  private readonly sourceTextures: readonly ResolvedTexture[]
  private readonly destinationTextures: readonly ResolvedTexture[]
  private readonly parameters = new Map<string, unknown>()
  private target: GSplatProcessingTargetLeaf | null = null
  private pipeline: GSplatProcessingPipelineLeaf | null = null
  private pass: GSplatProcessingPassLeaf | null = null
  private generation = 1
  private inFlight = 0
  private disposed = false

  constructor(
    adapter: GSplatGraphicsAdapter,
    slot: GSplatProcessingSlot,
    source: GSplatProcessorBinding,
    destination: GSplatProcessorBinding,
    options: GSplatProcessorOptions,
    invalidate?: GSplatProcessorInvalidation
  ) {
    this.adapter = adapter
    this.slot = slot
    this.invalidate = invalidate
    this.label = options.label?.trim() || PROCESSOR_LABEL
    this.adapterGeneration = adapter.generation

    let sourceLease: GSplatResourceLease | null = null
    let destinationLease: GSplatResourceLease | null = null
    let ownedSourceLease: GSplatResourceLease | null = null
    let ownedDestinationLease: GSplatResourceLease | null = null
    try {
      const sourceOwner = this.resolveBindingOwner(source, 'source')
      const destinationOwner = this.resolveBindingOwner(destination, 'destination')
      sourceLease = sourceOwner.acquire()
      destinationLease = destinationOwner.acquire()
      this.source = this.resolveBinding(source, sourceLease)
      this.destination = this.resolveBinding(destination, destinationLease)
      ownedSourceLease = this.source.resource
      ownedDestinationLease = this.destination.resource
      sourceLease = null
      destinationLease = null
      this.sourceFormat = this.requireFormat(this.source, 'source')
      this.destinationFormat = this.requireFormat(this.destination, 'destination')
      this.sourceFormatHash = this.sourceFormat.hash
      this.destinationFormatHash = this.destinationFormat.hash
      this.assertDestinationRevisionSupport()

      this.outputStreams = Object.freeze(
        this.resolveOutputStreams(destination.streams, this.destinationFormat)
      )
      this.inputStreams = Object.freeze(
        this.resolveInputStreams(source.streams, this.sourceFormat, this.outputStreams)
      )
      this.assertNoOverlap(this.source, this.destination, this.inputStreams, this.outputStreams)
      this.sourceTextures = Object.freeze(
        this.resolveTextures(this.source, this.inputStreams, 'source')
      )
      this.destinationTextures = Object.freeze(
        this.resolveTextures(this.destination, this.outputStreams, 'destination')
      )
      this.preflightTextures()

      const descriptor = this.createPipelineDescriptor(options)
      this.pipelineKey = descriptor.key
      this.stageLeaves(descriptor)
      ownedSourceLease = null
      ownedDestinationLease = null
    } catch (error) {
      this.disposeLeaves()
      sourceLease?.dispose()
      destinationLease?.dispose()
      ownedSourceLease?.dispose()
      ownedDestinationLease?.dispose()
      throw error
    }
  }

  setParameter(name: string, data: unknown): void {
    this.assertActive()
    if (!name) throw new Error('GSplatProcessor parameter name 不能为空')
    this.parameters.set(name, data)
  }

  getParameter(name: string): unknown {
    return this.parameters.get(name)
  }

  deleteParameter(name: string): void {
    this.assertActive()
    this.parameters.delete(name)
  }

  /** 在 Prepare processing slot 排队；调用时冻结所有可变输入。 */
  process(): Promise<void> {
    this.assertActive()
    const snapshot = this.createSnapshot()
    return this.slot.enqueue({
      label: this.label,
      run: () => this.executeSnapshot(snapshot),
    })
  }

  destroy(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    this.parameters.clear()
    if (this.inFlight === 0) this.disposeOwnedState()
  }

  dispose(): void {
    this.destroy()
  }

  private async executeSnapshot(snapshot: ProcessSnapshot): Promise<void> {
    this.assertSnapshotCurrent(snapshot)
    const pass = this.pass
    if (!pass) throw this.staleError('processing pass 已释放')
    this.inFlight++
    try {
      await pass.execute({
        textureBindings: snapshot.textureBindings,
        parameters: snapshot.parameters,
        blendState: snapshot.blendState,
      })
      this.assertSnapshotCurrent(snapshot)
      this.commitDestinationRevision()
    } finally {
      this.inFlight--
      if (this.disposed && this.inFlight === 0) this.disposeOwnedState()
    }
  }

  private resolveBindingOwner(binding: GSplatProcessorBinding, label: string): GSplatResourceLease {
    const resource = binding.resource ?? binding.handleBinding?.resource
    if (!resource) {
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_INVALID_BINDING',
        `GSplatProcessor ${label} 缺少 Resource/Handle binding`
      )
    }
    return resource
  }

  private resolveBinding(
    input: GSplatProcessorBinding,
    resource: GSplatResourceLease
  ): ResolvedBinding {
    return Object.freeze({
      input,
      resource,
      identity: resource.resourceIdentity ?? resource,
      generation: resource.generation,
      handleGeneration: input.handleBinding?.generation ?? null,
    })
  }

  private requireFormat(binding: ResolvedBinding, label: string): GSplatFormat {
    const format = binding.resource.format
    if (!format || !binding.resource.streams || !binding.resource.getTexture) {
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_INVALID_BINDING',
        `GSplatProcessor ${label} Resource 不支持 Unified texture streams`
      )
    }
    return format
  }

  private assertDestinationRevisionSupport(): void {
    const handle = this.destination.input.handleBinding
    const handleOwnsResource =
      handle && (handle.resource.resourceIdentity ?? handle.resource) === this.destination.identity
    if (
      (handleOwnsResource && handle.markStreamsWritten) ||
      this.destination.resource.markStreamsWritten
    ) {
      return
    }
    throw new GSplatProcessorError(
      'GSPLAT_PROCESSOR_INVALID_BINDING',
      'GSplatProcessor destination Resource 不支持 content revision'
    )
  }

  private resolveOutputStreams(
    requested: readonly string[] | undefined,
    format: GSplatFormat
  ): GSplatStreamDescriptor[] {
    if (!requested || requested.length === 0) {
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_INVALID_STREAM',
        'GSplatProcessor destination streams 必填且非空'
      )
    }
    this.assertUniqueNames(requested, 'destination')
    const selected = new Set(requested)
    const result = [...format.streams, ...format.extraStreams].filter((stream) =>
      selected.has(stream.name)
    )
    if (result.length !== selected.size) {
      const missing = requested.filter((name) => !format.getStream(name))
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_INVALID_STREAM',
        `GSplatProcessor destination stream 不存在: ${missing.join(', ')}`
      )
    }
    // MRT slot 保持调用方声明顺序，与冻结上游 destination.streams 一致。
    return requested.map((name) => result.find((stream) => stream.name === name)!)
  }

  private resolveInputStreams(
    requested: readonly string[] | undefined,
    format: GSplatFormat,
    outputs: readonly GSplatStreamDescriptor[]
  ): GSplatStreamDescriptor[] {
    const all = [...format.streams, ...format.extraStreams]
    if (!requested || requested.length === 0) {
      if (this.sameResourceOwners()) {
        const outputNames = new Set(outputs.map((stream) => stream.name))
        return all.filter((stream) => !outputNames.has(stream.name))
      }
      return all
    }
    this.assertUniqueNames(requested, 'source')
    const selected = new Set(requested)
    const result = all.filter((stream) => selected.has(stream.name))
    if (result.length !== selected.size) {
      const missing = requested.filter((name) => !format.getStream(name))
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_INVALID_STREAM',
        `GSplatProcessor source stream 不存在: ${missing.join(', ')}`
      )
    }
    // input declaration 必须使用 format canonical 顺序，而不是调用方数组顺序。
    return result
  }

  private assertNoOverlap(
    source: ResolvedBinding,
    destination: ResolvedBinding,
    inputs: readonly GSplatStreamDescriptor[],
    outputs: readonly GSplatStreamDescriptor[]
  ): void {
    if (source.identity !== destination.identity) return
    const outputNames = new Set(outputs.map((stream) => stream.name))
    const overlap = inputs
      .filter((stream) => outputNames.has(stream.name))
      .map((stream) => stream.name)
    if (overlap.length > 0) {
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_READ_WRITE_OVERLAP',
        `GSplatProcessor 同一 Resource 禁止读写同名 stream: ${overlap.join(', ')}`
      )
    }
  }

  private resolveTextures(
    binding: ResolvedBinding,
    streams: readonly GSplatStreamDescriptor[],
    label: string
  ): ResolvedTexture[] {
    return streams.map((descriptor) => {
      let texture: GSplatTextureLeaf | null = null
      if (descriptor.storage === 'instance' && binding.input.handleBinding) {
        const handleResource = binding.input.handleBinding.resource
        if ((handleResource.resourceIdentity ?? handleResource) !== binding.identity) {
          throw new GSplatProcessorError(
            'GSPLAT_PROCESSOR_INVALID_BINDING',
            `GSplatProcessor ${label} instance stream 的 Resource 与 Handle 不一致`
          )
        }
        texture = binding.input.handleBinding.getInstanceTexture?.(descriptor.name) ?? null
      }
      texture ??= binding.resource.getTexture?.(descriptor.name) ?? null
      if (!texture) {
        throw new GSplatProcessorError(
          'GSPLAT_PROCESSOR_INVALID_STREAM',
          `GSplatProcessor ${label} texture 不存在: ${descriptor.name}`
        )
      }
      if (texture.format !== descriptor.format) {
        throw new GSplatProcessorError(
          'GSPLAT_PROCESSOR_INVALID_STREAM',
          `GSplatProcessor ${label} texture format 不匹配: ${descriptor.name}`
        )
      }
      return Object.freeze({ name: descriptor.name, descriptor, texture })
    })
  }

  private preflightTextures(): void {
    const outputs = this.destinationTextures
    if (outputs.length > this.adapter.limits.maxColorAttachments) {
      throw this.limitError(
        `MRT attachments ${outputs.length} > ${this.adapter.limits.maxColorAttachments}`
      )
    }
    const first = outputs[0]?.texture
    let bytesPerSample = 0
    for (const output of outputs) {
      const info = this.adapter.getTextureFormatInfo(output.texture.format)
      if (!info.renderable) {
        throw this.limitError(`destination format 不可渲染: ${output.texture.format}`)
      }
      if (output.texture.width !== first?.width || output.texture.height !== first.height) {
        throw this.limitError('destination attachments 尺寸不一致')
      }
      bytesPerSample += info.bytesPerTexel
      this.validateTexture(output.texture, true)
    }
    if (bytesPerSample > this.adapter.limits.maxColorAttachmentBytesPerSample) {
      throw this.limitError(
        `MRT bytes/sample ${bytesPerSample} > ${this.adapter.limits.maxColorAttachmentBytesPerSample}`
      )
    }
    for (const input of this.sourceTextures) this.validateTexture(input.texture, false)
    this.validateResourceCapacity(this.source, 'source')
    this.validateResourceCapacity(this.destination, 'destination')
  }

  private validateTexture(texture: GSplatTextureLeaf, renderAttachment: boolean): void {
    const { maxTextureDimension2D } = this.adapter.limits
    if (
      !Number.isSafeInteger(texture.width) ||
      !Number.isSafeInteger(texture.height) ||
      texture.width < 1 ||
      texture.height < 1 ||
      texture.width > maxTextureDimension2D ||
      texture.height > maxTextureDimension2D
    ) {
      throw this.limitError(`texture dimension 非法: ${texture.label}`)
    }
    this.adapter.validateTextureDescriptor({
      label: `${this.label}:preflight:${texture.label}`,
      width: texture.width,
      height: texture.height,
      format: texture.format,
      usages: renderAttachment
        ? ['sampled', 'render-attachment', 'copy-src', 'copy-dst']
        : ['sampled'],
    })
  }

  private validateResourceCapacity(binding: ResolvedBinding, label: string): void {
    const dimensions = binding.resource.streams?.textureDimensions
    if (!dimensions) throw this.limitError(`${label} 缺少 texture dimensions`)
    const capacity = dimensions.width * dimensions.height
    if (!Number.isSafeInteger(capacity) || binding.resource.numSplats > capacity) {
      throw this.limitError(
        `${label} splat count 超出 texture capacity: ${binding.resource.numSplats} > ${capacity}`
      )
    }
  }

  private createPipelineDescriptor(
    options: GSplatProcessorOptions
  ): GSplatProcessingPipelineDescriptor {
    const language = this.adapter.language
    const source = language === 'wgsl' ? options.processWGSL : options.processGLSL
    this.validateProcessSource(language, source)
    const defines = new Map<string, unknown>()
    this.source.resource.configureMaterialDefines?.(defines)
    defines.set('SH_BANDS', '0')
    const inputNames = this.inputStreams.map((stream) => stream.name)
    const inputDeclarations = this.sourceFormat.getInputDeclarations(inputNames, language)
    const readCode =
      this.source.input.streams && this.source.input.streams.length > 0
        ? ''
        : this.sourceFormat.getReadCode(language)
    const outputDeclarations = this.destinationFormat.getOutputDeclarations(
      this.outputStreams,
      language
    )
    const canonical = JSON.stringify({
      language,
      source,
      mode: this.source.input.streams?.length ? 'explicit' : 'auto',
      inputs: this.inputStreams.map(canonicalDescriptor),
      outputs: this.outputStreams.map(canonicalDescriptor),
      sourceFormatHash: this.sourceFormat.hash,
      destinationFormatHash: this.destinationFormat.hash,
      defines: [...defines],
      inputDeclarations,
      readCode,
      outputDeclarations,
    })
    const key = `${this.label}:${fnv1a(canonical).toString(16)}:${canonical}`
    return Object.freeze({
      label: this.label,
      key,
      language,
      processSource: source!,
      inputDeclarations,
      readCode,
      outputDeclarations,
      inputStreams: Object.freeze(this.inputStreams.map(freezePipelineDescriptor)),
      outputStreams: Object.freeze(this.outputStreams.map(freezePipelineDescriptor)),
      outputTypes: Object.freeze(
        this.outputStreams.map(
          (stream) => this.adapter.getTextureFormatInfo(stream.format).shaderValueType
        )
      ),
      defines: Object.freeze([...defines].map((entry) => Object.freeze(entry))),
    })
  }

  private stageLeaves(descriptor: GSplatProcessingPipelineDescriptor): void {
    let target: GSplatProcessingTargetLeaf | null = null
    let pipeline: GSplatProcessingPipelineLeaf | null = null
    let pass: GSplatProcessingPassLeaf | null = null
    try {
      target = this.adapter.createPrivateProcessingTarget({
        label: `${this.label}:target`,
        attachments: Object.freeze(this.destinationTextures.map((entry) => entry.texture)),
      })
      pipeline = this.adapter.createPrivateProcessingPipeline(descriptor)
      pass = this.adapter.createPrivateProcessingPass({
        label: `${this.label}:pass`,
        target,
        pipeline,
      })
      this.target = target
      this.pipeline = pipeline
      this.pass = pass
    } catch (error) {
      pass?.dispose()
      pipeline?.dispose()
      target?.dispose()
      throw error
    }
  }

  private createSnapshot(): ProcessSnapshot {
    const sourceDimensions = this.source.resource.streams!.textureDimensions
    const destinationDimensions = this.destination.resource.streams!.textureDimensions
    const parameters: Array<readonly [string, unknown]> = [
      ['splatTextureSize', sourceDimensions.width],
      ['dstTextureSize', destinationDimensions.width],
      ['srcNumSplats', this.source.resource.numSplats],
      ['dstNumSplats', this.destination.resource.numSplats],
    ]
    for (const [name, value] of this.source.resource.parameters ?? []) {
      parameters.push(Object.freeze([name, snapshotValue(value)] as const))
    }
    // 保留顺序和重复 key，使 adapter 严格执行“Resource 参数后由用户参数覆盖”。
    for (const [name, value] of this.parameters) {
      parameters.push(Object.freeze([name, snapshotValue(value)] as const))
    }
    const snapshot = Object.freeze({
      processorGeneration: this.generation,
      adapterGeneration: this.adapterGeneration,
      sourceGeneration: this.source.generation,
      destinationGeneration: this.destination.generation,
      sourceHandleGeneration: this.source.handleGeneration,
      destinationHandleGeneration: this.destination.handleGeneration,
      sourceFormatHash: this.sourceFormatHash,
      destinationFormatHash: this.destinationFormatHash,
      textureBindings: Object.freeze(
        this.sourceTextures.map(({ name, texture }) => Object.freeze({ name, texture }))
      ),
      parameters: Object.freeze(parameters),
      blendState: snapshotValue(this.blendState),
    })
    this.assertSnapshotCurrent(snapshot)
    return snapshot
  }

  private assertSnapshotCurrent(snapshot: ProcessSnapshot): void {
    if (this.disposed || snapshot.processorGeneration !== this.generation) {
      throw this.staleError('processor 已销毁或 generation 失效')
    }
    if (
      snapshot.adapterGeneration !== this.adapter.generation ||
      this.target?.generation !== this.adapter.generation ||
      this.pipeline?.generation !== this.adapter.generation ||
      this.pass?.generation !== this.adapter.generation
    ) {
      throw this.staleError('graphics adapter generation 已失效')
    }
    if (
      snapshot.sourceGeneration !== this.source.resource.generation ||
      snapshot.destinationGeneration !== this.destination.resource.generation ||
      snapshot.sourceHandleGeneration !== (this.source.input.handleBinding?.generation ?? null) ||
      snapshot.destinationHandleGeneration !==
        (this.destination.input.handleBinding?.generation ?? null) ||
      snapshot.sourceFormatHash !== this.sourceFormat.hash ||
      snapshot.destinationFormatHash !== this.destinationFormat.hash
    ) {
      throw this.staleError('Resource/Handle/Format generation 已失效')
    }
    for (const entry of [...this.sourceTextures, ...this.destinationTextures]) {
      const current = this.resolveCurrentTexture(entry, this.bindingForTexture(entry))
      if (current !== entry.texture) throw this.staleError(`texture 已替换: ${entry.name}`)
    }
  }

  private bindingForTexture(entry: ResolvedTexture): ResolvedBinding {
    return this.destinationTextures.includes(entry) ? this.destination : this.source
  }

  private resolveCurrentTexture(
    entry: ResolvedTexture,
    binding: ResolvedBinding
  ): GSplatTextureLeaf | null {
    try {
      if (entry.descriptor.storage === 'instance' && binding.input.handleBinding) {
        const instance = binding.input.handleBinding.getInstanceTexture?.(entry.name)
        if (instance) return instance
      }
      return binding.resource.getTexture?.(entry.name) ?? null
    } catch {
      return null
    }
  }

  private commitDestinationRevision(): void {
    const names = Object.freeze(this.outputStreams.map((stream) => stream.name))
    const handle = this.destination.input.handleBinding
    const handleOwnsResource =
      handle && (handle.resource.resourceIdentity ?? handle.resource) === this.destination.identity
    if (handleOwnsResource && handle.markStreamsWritten) handle.markStreamsWritten(names)
    else {
      const mark = this.destination.resource.markStreamsWritten
      if (!mark) {
        throw new GSplatProcessorError(
          'GSPLAT_PROCESSOR_INVALID_BINDING',
          'destination Resource 不支持 content revision'
        )
      }
      mark(names)
    }
    this.invalidate?.(this.destination.input, names)
  }

  private sameResourceOwners(): boolean {
    return this.source.identity === this.destination.identity
  }

  private assertUniqueNames(names: readonly string[], label: string): void {
    const unique = new Set(names)
    if (unique.size !== names.length) {
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_INVALID_STREAM',
        `GSplatProcessor ${label} streams 存在重复项`
      )
    }
  }

  private validateProcessSource(language: GSplatShaderLanguage, source: string | undefined): void {
    const entry = language === 'wgsl' ? /\bfn\s+process\s*\(\s*\)/ : /\bvoid\s+process\s*\(\s*\)/
    if (!source?.trim() || !entry.test(source)) {
      throw new GSplatProcessorError(
        'GSPLAT_PROCESSOR_SHADER_INVALID',
        `GSplatProcessor 缺少有效 ${language.toUpperCase()} process() entry`
      )
    }
  }

  private limitError(message: string): GSplatProcessorError {
    return new GSplatProcessorError('GSPLAT_PROCESSOR_LIMIT_EXCEEDED', message)
  }

  private staleError(message: string): GSplatProcessorError {
    return new GSplatProcessorError('GSPLAT_PROCESSOR_STALE', message)
  }

  private assertActive(): void {
    if (this.disposed) {
      throw new GSplatProcessorError('GSPLAT_PROCESSOR_DISPOSED', 'GSplatProcessor 已销毁')
    }
  }

  private disposeOwnedState(): void {
    this.disposeLeaves()
    this.source.resource.dispose()
    this.destination.resource.dispose()
  }

  private disposeLeaves(): void {
    this.pass?.dispose()
    this.pass = null
    this.pipeline?.dispose()
    this.pipeline = null
    this.target?.dispose()
    this.target = null
  }
}

function canonicalDescriptor(stream: GSplatStreamDescriptor): readonly string[] {
  return Object.freeze([stream.name, stream.format, stream.storage ?? 'resource'])
}

function freezePipelineDescriptor(stream: GSplatStreamDescriptor): Readonly<{
  name: string
  format: GSplatStreamDescriptor['format']
  storage: 'resource' | 'instance'
}> {
  return Object.freeze({
    name: stream.name,
    format: stream.format,
    storage: stream.storage ?? 'resource',
  })
}

function fnv1a(value: string): number {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

function snapshotValue(value: unknown): unknown {
  if (Array.isArray(value)) return Object.freeze(value.map(snapshotValue))
  if (ArrayBuffer.isView(value)) {
    if (value instanceof DataView) {
      return new DataView(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength))
    }
    const copy = (value as unknown as { slice?: () => ArrayBufferView }).slice?.()
    return copy ?? value
  }
  if (
    value &&
    typeof value === 'object' &&
    !('dispose' in value) &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    const result: Record<string, unknown> = {}
    for (const [name, entry] of Object.entries(value)) result[name] = snapshotValue(entry)
    return Object.freeze(result)
  }
  return value
}
